// pages/EnvironmentalSummary.jsx

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../../../shared/API/apiClient";
import { useReportingYear } from "../../../context/ReportingYearContext";
import { Zap } from "lucide-react";
import { useLocation } from "react-router-dom";

// ── Helpers ────────────────────────────────────────────────────────
const formatNumber = (value) => {
  if (value === null || value === undefined) return "—";
  const num = parseFloat(value);
  if (isNaN(num)) return "—";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

// ── sessionStorage helpers ─────────────────────────────────────────
const storageKey = (reportingYearId) =>
  `env_summary_entity_${reportingYearId}`;

const readPersistedEntity = (reportingYearId) => {
  if (!reportingYearId) return "";
  try {
    return sessionStorage.getItem(storageKey(reportingYearId)) || "";
  } catch {
    return "";
  }
};

const writePersistedEntity = (reportingYearId, entityId) => {
  if (!reportingYearId) return;
  try {
    if (entityId) {
      sessionStorage.setItem(storageKey(reportingYearId), entityId);
    } else {
      sessionStorage.removeItem(storageKey(reportingYearId));
    }
  } catch {
    // ignore
  }
};

// ── Map known scope keys → navigation links ────────────────────────
const SCOPE_LINKS = {
  energy_consumption: { 
    label: "Energy Consumption Entry", 
    path: "/dashboard/energy-breakdown-listing" 
  },
  scope1: { 
    label: "Scope1 Emissions Entry", 
    path: "/dashboard/scope1-activity-listing" 
  },
  scope2: { 
    label: "Scope2 Emissions Entry", 
    path: "/dashboard/scope2-activity-listing" 
  },
  emission_intensity: { 
    label: "Emission Intensity Details", 
    path: "/dashboard/emission-intensity-listing" 
  },
 
};

// ── Build card list purely from API response ───────────────────────
const buildCards = (apiData, selectedEntity) => {
  if (!apiData) return [];
  const cards = [];

  // Card 1: Total Energy
  cards.push({
    title: apiData.energy_lable ?? "Total Energy",
    subtitle: apiData.energy_subtitle ?? "From electricity, fuel & all energy sources",
    value: formatNumber(apiData.total_energy),
    unit: "GJ",
    scope: "energy_consumption", // Matches SCOPE_LINKS key
    entityId: selectedEntity,
  });

  // Dynamic scope cards (Scope 1, 2, etc.)
  (apiData.scopes ?? [])
    .filter((s) => s.scope && s.scope !== "none" && s.title)
    .forEach((s) => {
      cards.push({
        title: s.title,
        subtitle: s.sub_title ?? "Scope",
        value: formatNumber(s.total_emissions),
        unitJsx: <span>tCO<sub>2</sub>e</span>,
        scope: s.scope, // Matches SCOPE_LINKS keys (scope1, scope2)
        entityId: selectedEntity,
      });
    });

  // Last card: Emission Intensity
  cards.push({
    title: apiData.emission_intensity_lable ?? "Emission Intensity",
    subtitle: apiData.emission_subtitle ?? "Emission Intensity",
    value: formatNumber(apiData.emission_intensity),
    unitJsx: <span>tCO<sub>2</sub>e / GJ</span>,
    scope: "emission_intensity", // Matches SCOPE_LINKS key
    entityId: selectedEntity,
  });

  return cards;
};

const MetricCard = ({
  title,
  subtitle,
  value,
  unit,
  unitJsx,
  linkLabel,
  scope,
  entityId,
  loading,
}) => {
  const navigate = useNavigate();

  // Find the correct path based on the scope key
  const navConfig = SCOPE_LINKS[scope];

  return (
    <div className="metric-card">
      <div className="metric-card__header">
        <span className="metric-card__title">{title}</span>
        <span className="metric-card__icon"><Zap size={16} /></span>
      </div>

      <div className="metric-card__value-row">
        <span className="metric-card__value">{loading ? "—" : value}</span>
        <span className="metric-card__unit">{unitJsx ?? unit}</span>
      </div>

      <div className="metric-card__desc">{subtitle}</div>

      {/* Logic updated to use dynamic paths */}
      {scope && navConfig?.path && (
        <button
          className="metric-card__link"
          onClick={() =>
            navigate(navConfig.path, {
              state: { scope, title, entityId },
            })
          }
        >
          View Breakdown
          <span className="metric-card__arrow">→</span>
        </button>
      )}
    </div>
  );
};

// ── Page ───────────────────────────────────────────────────────────
const EnvironmentalSummaryPage = () => {
  const { reportingYear, reportingYearId } = useReportingYear();
  const location = useLocation();
  const [summaryData,    setSummaryData]    = useState(null);
  const [loading,        setLoading]        = useState(false);
  const [legalEntities,  setLegalEntities]  = useState([]);
  const [error,          setError]          = useState(null);

  const [selectedEntity, setSelectedEntity] = useState(() =>
    readPersistedEntity(reportingYearId)
  );

  const handleEntityChange = (entityId) => {
    setSelectedEntity(entityId);
    writePersistedEntity(reportingYearId, entityId);
  };

  // ── Fetch legal entities ────────────────────────────────────────
  useEffect(() => {
    if (!reportingYearId) return;
    const fetchLegalEntities = async () => {
      try {
        const res = await apiClient.get(`/api/legal-entities-by-year`, {
          params: { reporting_year_id: reportingYearId },
        });
        if (res.data.code === 200) {
        const entities = res.data.data || [];
        setLegalEntities(entities);
        const entityId = location.state?.legalEntityId;
        if (
            entityId &&
            entities.some(e => Number(e.entity_id) === Number(entityId))
        ) {
            setSelectedEntity(String(entityId));
        }
        } else {
          setLegalEntities([]);
        }
      } catch {
        setLegalEntities([]);
      }
    };
    fetchLegalEntities();
  }, [reportingYearId]);

  // ── Restore persisted entity when year changes ──────────────────
  useEffect(() => {
    const persisted = readPersistedEntity(reportingYearId);
    setSelectedEntity(persisted);
    if (!persisted) {
      setSummaryData(null);
      setError(null);
    }
  }, [reportingYearId]);

  // ── Fetch summary ───────────────────────────────────────────────
  useEffect(() => {
    if (!reportingYearId || !selectedEntity) return;

    const fetchSummary = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiClient.get(`/api/environmental-summary`, {
          params: {
            reporting_year_id: reportingYearId,
            legal_entity_id:   selectedEntity,
          },
        });
        const json = response.data;
        if (!json.success) {
          throw new Error(json.message || "Failed to fetch environmental summary.");
        }
        setSummaryData(json);
      } catch (err) {
        setError(err.message || "An unexpected error occurred.");
        setSummaryData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchSummary();
  }, [reportingYearId, selectedEntity]);

  // Pass selectedEntity into buildCards so Emission Intensity card can forward it
  const cards = buildCards(summaryData, selectedEntity);

  return (
    <div className="dash-page">
      <div className="dash-page-title">Environmental Summary</div>
      <div className="dash-page-subtitle">
        Consolidated environmental metrics for BRSR Principle 6 disclosures
      </div>

      {/* ── Filter card ── */}
      <div className="filter-card">
        <div className="filter-group">
          <label>Reporting Year *</label>
          <input
            className="filter-input"
            value={reportingYear || ""}
            disabled
          />
        </div>

        <div className="filter-group">
          <label>Legal Entity *</label>
          <select
            className="filter-input"
            value={selectedEntity}
            onChange={(e) => handleEntityChange(e.target.value)}
          >
            <option value="">Select</option>
            {legalEntities.map((entity) => (
              <option key={entity.entity_id} value={entity.entity_id}>
                {entity.entity_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Error banner ── */}
      {error && (
        <div className="env-error-banner">
          <span className="env-error-icon">⚠</span> {error}
        </div>
      )}

      {/* ── Prompt when no entity selected ── */}
      {!selectedEntity && !loading && (
         <div className="empty-entity">
          <div className="empty-entity__icon"><i className="fa-solid fa-building" /></div>
          <div className="empty-entity__title">Select a Legal Entity to continue</div>
          <div className="empty-entity__sub">
            Choose a legal entity from the filter above to view the environmental summary.
          </div>
          
          
        
        </div>
      )}

      {/* ── Metric cards grid ── */}
      {(selectedEntity && (loading || cards.length > 0)) && (
        <div className="env-grid">
          {loading
            ? Array.from({ length: cards.length || 4 }).map((_, i) => (
                <MetricCard key={i} title="—" value="—" unit="" loading={true} />
              ))
            : cards.map((card, i) => (
                <MetricCard key={i} {...card} loading={false} />
              ))}
        </div>
      )}
    </div>
  );
};

export default EnvironmentalSummaryPage;
